import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SearchAPI, SearchResult } from "../api/searchApi";
import { ProductAPI } from "../api/productApi";
import { BasketAPI } from "../api/basketApi";
import { Disclosure, DisclosureButton, DisclosurePanel, Button } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { ShoppingCart } from "lucide-react";

export enum ProviderType {
  None = 0,
  Shate = 1,
  Rossko = 2,
}

interface PriceItem {
  value: number;
  available: number;
  deliveryDate: string;
  providerType: ProviderType;
  providerOrderInfo?: string;
  temporaryPriceGuid?: string;
  partUniqueId?: string;
}

interface ProductVariant {
  brand: string;
  code: string;
  uniqueId: string;
  prices: PriceItem[];
  lowestPrice: number;
  closestDeliveryDate: string;
}

interface ProductPrices {
  original: ProductVariant[];
  analogs: ProductVariant[];
}

interface AddedToCartItem {
  added: boolean;
  response: {
    id: number;
  };
}

interface MultiSearchResult {
  term: string;
  results: SearchResult[];
  loading: boolean;
  error?: string;
  prices?: { [uniqueId: string]: ProductPrices };
  loadingPrices?: { [uniqueId: string]: boolean };
  globalMinPrice?: number;
  globalMinDate?: Date | null;
}

const MultiSearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<MultiSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState<Record<string, AddedToCartItem>>({});
  const [addingToCart, setAddingToCart] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const termsParam = searchParams.get("terms");
    const selectedParam = searchParams.get("selected");
    
    if (termsParam) {
      try {
        const terms = JSON.parse(decodeURIComponent(termsParam));
        setSearchTerms(terms);
        
        if (selectedParam) {
          // Если есть выбранные запчасти, работаем только с ними
          const selectedUniqueIds = JSON.parse(decodeURIComponent(selectedParam));
          performMultiSearchWithSelected(terms, selectedUniqueIds);
        } else {
          // Обычный мультипоиск
          performMultiSearch(terms);
        }
      } catch (error) {
        console.error("Ошибка парсинга параметров:", error);
        navigate("/");
      }
    } else {
      navigate("/");
    }
  }, [searchParams, navigate]);

  const handleAddToCart = async (item: PriceItem, uid: string) => {
    const priceKey = item.temporaryPriceGuid || '';
    
    // Устанавливаем состояние загрузки
    setAddingToCart(prev => ({
      ...prev,
      [priceKey]: true
    }));

    try {
      const response = await BasketAPI.addItem(uid, {
        value: item.value,
        available: item.available,
        deliveryDate: item.deliveryDate,
        providerType: item.providerType,
        providerOrderInfo: item.providerOrderInfo,
        temporaryPriceGuid: item.temporaryPriceGuid,
      });

      setAddedToCart(prev => ({
        ...prev,
        [priceKey]: {
          added: true,
          response: response.data,
        },
      }));
    } catch (error) {
      console.error("Ошибка добавления в корзину:", error);
    } finally {
      // Убираем состояние загрузки
      setAddingToCart(prev => ({
        ...prev,
        [priceKey]: false
      }));
    }
  };



  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    
    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) {
      return <span className="text-gray-500">Не указана</span>;
    }
    
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return <span className="text-green-600 font-medium">Сегодня</span>;
    }
    
    return <span className="text-gray-500">{date.toLocaleDateString()}</span>;
  };

  const loadPricesForResult = async (resultIndex: number, uniqueId: string) => {
    setSearchResults(prev => prev.map((item, index) => {
      if (index === resultIndex) {
        return {
          ...item,
          loadingPrices: {
            ...item.loadingPrices,
            [uniqueId]: true
          }
        };
      }
      return item;
    }));

    try {
      const pricesData = await ProductAPI.getProductPrices(uniqueId);
      
      setSearchResults(prev => prev.map((item, index) => {
        if (index === resultIndex) {
          const newPrices = {
            ...item.prices,
            [uniqueId]: pricesData
          };
          
          // Пересчитываем глобальные минимальные значения для всех загруженных цен
          const allPrices = Object.values(newPrices);
          if (allPrices.length > 0) {
            const allOriginalPrices = allPrices.flatMap(p => p.original.map((v: any) => v.lowestPrice));
            const allAnalogPrices = allPrices.flatMap(p => p.analogs.map((v: any) => v.lowestPrice));
            const allPricesArray = [...allOriginalPrices, ...allAnalogPrices];
            
            const allOriginalDates = allPrices.flatMap(p => p.original.map((v: any) => new Date(v.closestDeliveryDate).getTime()));
            const allAnalogDates = allPrices.flatMap(p => p.analogs.map((v: any) => new Date(v.closestDeliveryDate).getTime()));
            const allDatesArray = [...allOriginalDates, ...allAnalogDates];
            
            const globalMinPrice = allPricesArray.length > 0 ? Math.min(...allPricesArray) : undefined;
            const globalMinDate = allDatesArray.length > 0 ? new Date(Math.min(...allDatesArray)) : null;
            
            return {
              ...item,
              prices: newPrices,
              loadingPrices: {
                ...item.loadingPrices,
                [uniqueId]: false
              },
              globalMinPrice,
              globalMinDate
            };
          }
          
          return {
            ...item,
            prices: newPrices,
            loadingPrices: {
              ...item.loadingPrices,
              [uniqueId]: false
            }
          };
        }
        return item;
      }));
    } catch (error) {
      console.error(`Ошибка загрузки цен для ${uniqueId}:`, error);
      setSearchResults(prev => prev.map((item, index) => {
        if (index === resultIndex) {
          return {
            ...item,
            loadingPrices: {
              ...item.loadingPrices,
              [uniqueId]: false
            }
          };
        }
        return item;
      }));
    }
  };

  const performMultiSearchWithSelected = async (terms: string[], selectedUniqueIds: string[]) => {
    setLoading(true);
    const results: MultiSearchResult[] = [];

    try {
      // Загружаем данные о запчастях по их uniqueId
      for (let i = 0; i < terms.length; i++) {
        const term = terms[i];
        const uniqueId = selectedUniqueIds[i];
        
        try {
          // Получаем информацию о запчасти
          const partInfo = await ProductAPI.getProductById(uniqueId);
          
          results.push({
            term,
            results: [{
              id: partInfo.id,
              uniqueId: partInfo.uniqueId,
              brandName: partInfo.brandName,
              code: partInfo.code,
              originalСode: partInfo.originalСode,
              brandOriginalName: partInfo.brandOriginalName,
              brandId: partInfo.brandId,
              name: partInfo.name,
              description: partInfo.description,
              providerInfo: partInfo.providerInfo,
              imageUpdated: partInfo.imageUpdated,
              contentId: partInfo.contentId,
              content: partInfo.content,
              shateLastSearch: partInfo.shateLastSearch,
              rosskoLastSearch: partInfo.rosskoLastSearch,
              autopiterLastSearch: partInfo.autopiterLastSearch
            }],
            loading: false,
          });
        } catch (error) {
          console.error(`Ошибка загрузки информации о запчасти ${uniqueId}:`, error);
          // Добавляем запчасть с базовой информацией
          results.push({
            term,
            results: [{
              id: 0,
              uniqueId,
              brandName: 'Ошибка загрузки',
              code: term,
              originalСode: term,
              brandOriginalName: '',
              brandId: 0,
              name: '',
              description: '',
              providerInfo: '',
              imageUpdated: false,
              contentId: null,
              content: null,
              shateLastSearch: null,
              rosskoLastSearch: null,
              autopiterLastSearch: null
            }],
            loading: false,
            error: 'Ошибка загрузки информации о запчасти'
          });
        }
      }
      
      // Сразу отображаем запчасти
      setSearchResults(results);
      setLoading(false);

      // Загружаем цены для всех запчастей параллельно
      const pricePromises = results.map((_, i) => loadPricesForResult(i, selectedUniqueIds[i]));
      await Promise.all(pricePromises);
    } catch (error) {
      console.error("Ошибка загрузки данных:", error);
      setLoading(false);
    }
  };

  const performMultiSearch = async (terms: string[]) => {
    setLoading(true);
    const results: MultiSearchResult[] = [];

    // Инициализируем результаты
    for (const term of terms) {
      results.push({
        term,
        results: [],
        loading: true,
      });
    }
    setSearchResults(results);

    try {
      // Выполняем мультипоиск через API
      const multiSearchResults = await SearchAPI.getMultiSearchResults(terms);
      console.log('MultiSearch API Response:', multiSearchResults);
      
      // Обновляем результаты
      const updatedResults = terms.map(term => {
        const searchResult = multiSearchResults[term];
        console.log(`Processing term "${term}":`, searchResult);
        
        const foundResults = searchResult?.parts || [];
        
        return {
          term,
          results: foundResults,
          loading: false,
          error: searchResult?.parts === null ? searchResult?.message : (foundResults.length === 0 ? searchResult?.message : undefined)
        };
      });
      
      console.log('Updated results:', updatedResults);
      setSearchResults(updatedResults);

      // Загружаем цены для найденных результатов в фоне
      for (let i = 0; i < updatedResults.length; i++) {
        const result = updatedResults[i];
        if (result.results.length > 0) {
          // Загружаем цены для каждой найденной запчасти
          for (const searchResult of result.results) {
            loadPricesForResult(i, searchResult.uniqueId);
          }
        }
      }
    } catch (error) {
      console.error("Ошибка мультипоиска:", error);
      setSearchResults(prev => prev.map(item => ({
        ...item,
        loading: false,
        error: "Ошибка поиска",
        results: []
      })));
    } finally {
      setLoading(false);
    }
  };


  const renderSearchResult = (result: MultiSearchResult, index: number) => {
    if (result.loading) {
      return (
        <div key={index} className="mb-6">
          <h2 className="text-lg font-semibold mb-3">{result.term}</h2>
          <div className="flex items-center justify-center py-4">
            <div className="loader mb-2"></div>
            <p className="text-gray-500 ml-3 text-sm">Поиск...</p>
          </div>
        </div>
      );
    }

    if (result.error) {
      return (
        <div key={index} className="mb-6">
          <h2 className="text-lg font-semibold mb-3">{result.term}</h2>
          <p className="text-red-500 text-sm">{result.error}</p>
        </div>
      );
    }

    if (result.results.length === 0) {
      return (
        <div key={index} className="mb-6">
          <h2 className="text-lg font-semibold mb-3">{result.term}</h2>
          <p className="text-gray-500 text-sm">Ничего не найдено</p>
        </div>
      );
    }

    return (
      <div key={index} className="mb-6">
        <h2 className="text-lg font-semibold mb-3">{result.term}</h2>
        
        {/* Информационное сообщение */}
        {result.error && result.results.length > 0 && (
          <p className="text-xs text-gray-600 mb-3">{result.error}</p>
        )}
        
        {/* Список найденных предложений */}
        <div className="w-full">
          <div className="space-y-1">
            {result.results.map((item, itemIndex) => (
              <Disclosure key={itemIndex} as="div">
                {({ open }) => (
                  <>
                    <DisclosureButton 
                      className="flex w-full justify-between items-center p-2 text-left text-sm font-medium bg-white border border-gray-200 rounded hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-gray-900 truncate">{item.brandName}</span>
                          <span className="text-gray-500 text-xs">•</span>
                          <span className="text-gray-600 text-xs truncate">{item.code}</span>
                        </div>
                        {item.name && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">{item.name}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        {/* Кружочек загрузки */}
                        {/* Индикатор загрузки цен для этой конкретной запчасти */}
                        {result.loadingPrices && result.loadingPrices[item.uniqueId] && (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                        )}
                        
                        {/* Минимальная цена для этой конкретной запчасти */}
                        {result.prices && result.prices[item.uniqueId] && !result.loadingPrices?.[item.uniqueId] && (() => {
                          const prices = result.prices[item.uniqueId];
                          const allPrices = [...prices.original.map(v => v.lowestPrice), ...prices.analogs.map(v => v.lowestPrice)];
                          const allDates = [...prices.original.map(v => new Date(v.closestDeliveryDate).getTime()), ...prices.analogs.map(v => new Date(v.closestDeliveryDate).getTime())];
                          
                          const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
                          const minDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : null;
                          
                          // Проверяем, есть ли предложения
                          const hasOffers = allPrices.length > 0 && minPrice > 0;
                          
                          if (!hasOffers) {
                            return (
                              <div className="text-right text-xs">
                                <div className="text-gray-500">
                                  Нет предложений
                                </div>
                              </div>
                            );
                          }
                          
                          return (
                            <div className="text-right text-xs">
                              <div className="font-semibold">
                                От {minPrice.toLocaleString()} ₽
                              </div>
                              <div className="text-gray-500">
                                Доставка: {minDate ? formatDate(minDate.toISOString()) : 'Не указана'}
                              </div>
                            </div>
                          );
                        })()}
                        
                        <a 
                          href={`/product?part_id=${item.uniqueId}`}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-full text-gray-700 bg-transparent hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-500"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Перейти
                        </a>
                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                      </div>
                    </DisclosureButton>
                    <DisclosurePanel className="px-2 py-1 text-sm text-gray-500 bg-gray-50 border-l border-r border-b border-gray-200 rounded-b">
                      {item.description && (
                        <p className="text-xs text-gray-600 mb-1">{item.description}</p>
                      )}                      
                        {/* Ценовые предложения */}
                        {result.loadingPrices && result.loadingPrices[item.uniqueId] ? (
                          <div className="flex items-center justify-center space-x-2 py-4">
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                            <span className="text-sm text-gray-500">Загрузка ценовых предложений...</span>
                          </div>
                        ) : result.prices?.[item.uniqueId] ? (() => {
                          const prices = result.prices[item.uniqueId];
                          const allPrices = [...prices.original.map(v => v.lowestPrice), ...prices.analogs.map(v => v.lowestPrice)];
                          const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
                          const hasOffers = allPrices.length > 0 && minPrice > 0;
                          
                          if (!hasOffers) {
                            return (
                              <div className="text-center py-4">
                                <p className="text-sm text-gray-500">Нет предложений</p>
                              </div>
                            );
                          }
                          
                          return (
                            <div className="space-y-3">
                              {result.prices[item.uniqueId].original.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-gray-900 mb-2">Оригинальные запчасти:</h4>
                              <div className="space-y-2">
                                {result.prices[item.uniqueId].original.map((variant, variantIndex) => (
                                  <Disclosure key={variantIndex} as="div">
                                    {({ open }) => (
                                      <>
                                        <DisclosureButton className="flex w-full justify-between items-center p-2 text-left text-sm font-medium bg-white border border-gray-200 rounded hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
                                          <div className="flex-1">
                                            <div className="flex items-center space-x-2">
                                              <span className="font-medium text-sm">{variant.brand}</span>
                                              <span className="text-gray-500 text-sm">{variant.code}</span>
                                            </div>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <div className="text-right">
                                              <div className="font-semibold text-gray-900 text-sm">
                                                От {variant.lowestPrice.toLocaleString()} ₽
                                              </div>
                                              <div className="text-xs">
                                                {formatDate(variant.closestDeliveryDate)}
                                              </div>
                                            </div>
                                            <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                                          </div>
                                        </DisclosureButton>
                                        <DisclosurePanel className="px-2 py-1 text-sm text-gray-500 bg-gray-50 border-l border-r border-b border-gray-200 rounded-b">
                                          {variant.prices.length > 0 ? (
                                            <div className="space-y-1">
                                              {variant.prices.map((price, priceIndex) => (
                                                <div key={priceIndex} className="flex justify-between items-center text-xs border-t pt-2 pb-2">
                                                  <div className="flex-1 flex items-center space-x-6">
                                                    <div className="w-20 text-left">
                                                      <span className="font-medium">{price.value.toLocaleString()} ₽</span>
                                                    </div>
                                                    <div className="w-24 text-left">
                                                      {formatDate(price.deliveryDate)}
                                                    </div>
                                                    <div className="w-20 text-left">
                                                      <span className="text-gray-600">
                                                        Доступно: {price.available}
                                                      </span>
                                                    </div>
                                                  </div>
                                                  <div className="ml-2">
                                                    {addedToCart[price.temporaryPriceGuid || '']?.added ? (
                                                      <div className="flex items-center gap-1 rounded bg-green-100 py-1 px-2 text-xs text-green-700">
                                                        <ShoppingCart className="size-3 shrink-0" />
                                                        <span>В корзине</span>
                                                      </div>
                                                    ) : addingToCart[price.temporaryPriceGuid || ''] ? (
                                                      <div className="flex items-center gap-1 rounded bg-blue-100 py-1 px-2 text-xs text-blue-700">
                                                        <div className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                                                        <span>Добавление...</span>
                                                      </div>
                                                    ) : (
                                                      <Button
                                                        className="flex items-center gap-1 rounded bg-gray-100 py-1 px-2 text-xs text-gray-500 data-[hover]:bg-gray-200"
                                                        onClick={() => handleAddToCart(price, price.partUniqueId || item.uniqueId)}
                                                      >
                                                        <ShoppingCart className="size-3 shrink-0 text-muted-foreground" />
                                                        <span>В корзину</span>
                                                      </Button>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="text-xs text-gray-500">Нет ценовых предложений</p>
                                          )}
                                        </DisclosurePanel>
                                      </>
                                    )}
                                  </Disclosure>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {result.prices[item.uniqueId].analogs.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-gray-700 mb-2">Аналоги:</h4>
                              <div className="space-y-2">
                                {result.prices[item.uniqueId].analogs.map((variant, variantIndex) => (
                                  <Disclosure key={variantIndex} as="div">
                                    {({ open }) => (
                                      <>
                                        <DisclosureButton className="flex w-full justify-between items-center p-2 text-left text-sm font-medium bg-white border border-gray-200 rounded hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
                                          <div className="flex-1">
                                            <div className="flex items-center space-x-2">
                                              <span className="font-medium text-sm">{variant.brand}</span>
                                              <span className="text-gray-500 text-sm">{variant.code}</span>
                                            </div>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <div className="text-right">
                                              <div className="font-semibold text-gray-900 text-sm">
                                                От {variant.lowestPrice.toLocaleString()} ₽
                                              </div>
                                              <div className="text-xs">
                                                {formatDate(variant.closestDeliveryDate)}
                                              </div>
                                            </div>
                                            <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                                          </div>
                                        </DisclosureButton>
                                        <DisclosurePanel className="px-2 py-1 text-sm text-gray-500 bg-gray-50 border-l border-r border-b border-gray-200 rounded-b">
                                          {variant.prices.length > 0 ? (
                                            <div className="space-y-1">
                                              {variant.prices.map((price, priceIndex) => (
                                                <div key={priceIndex} className="flex justify-between items-center text-xs border-t pt-2 pb-2">
                                                  <div className="flex-1 flex items-center space-x-6">
                                                    <div className="w-20 text-left">
                                                      <span className="font-medium">{price.value.toLocaleString()} ₽</span>
                                                    </div>
                                                    <div className="w-24 text-left">
                                                      {formatDate(price.deliveryDate)}
                                                    </div>
                                                    <div className="w-20 text-left">
                                                      <span className="text-gray-600">
                                                        Доступно: {price.available}
                                                      </span>
                                                    </div>
                                                  </div>
                                                  <div className="ml-2">
                                                    {addedToCart[price.temporaryPriceGuid || '']?.added ? (
                                                      <div className="flex items-center gap-1 rounded bg-green-100 py-1 px-2 text-xs text-green-700">
                                                        <ShoppingCart className="size-3 shrink-0" />
                                                        <span>В корзине</span>
                                                      </div>
                                                    ) : addingToCart[price.temporaryPriceGuid || ''] ? (
                                                      <div className="flex items-center gap-1 rounded bg-blue-100 py-1 px-2 text-xs text-blue-700">
                                                        <div className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                                                        <span>Добавление...</span>
                                                      </div>
                                                    ) : (
                                                      <Button
                                                        className="flex items-center gap-1 rounded bg-gray-100 py-1 px-2 text-xs text-gray-500 data-[hover]:bg-gray-200"
                                                        onClick={() => handleAddToCart(price, price.partUniqueId || item.uniqueId)}
                                                      >
                                                        <ShoppingCart className="size-3 shrink-0 text-muted-foreground" />
                                                        <span>В корзину</span>
                                                      </Button>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="text-xs text-gray-500">Нет ценовых предложений</p>
                                          )}
                                        </DisclosurePanel>
                                      </>
                                    )}
                                  </Disclosure>
                                ))}
                              </div>
                            </div>
                          )}
                            </div>
                          );
                        })() : (
                          <div className="text-xs text-gray-500">
                            Ценовые данные не загружены
                          </div>
                        )}
                    </DisclosurePanel>
                  </>
                )}
              </Disclosure>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-10 w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Мультипоиск</h1>
        <p className="text-gray-600">
          Поиск по {searchTerms.length} артикулам: {searchTerms.join(", ")}<br/>
          Раздел работает в тестовом режиме и может работать некорректно
        </p>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader mb-4"></div>
          <p className="text-gray-500">Выполняется поиск...</p>
        </div>
      ) : (
        <div className="space-y-8">
          {searchResults.map((result, index) => renderSearchResult(result, index))}
        </div>
      )}
    </div>
  );
};

export default MultiSearchPage;
